import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { ActivatedRoute } from '@angular/router';

import { PortalApiService } from '../../portal-api.service';
import { PortalAuthService } from '../../portal-auth.service';
import { PortalPreviewService } from '../../portal-preview.service';
import { portalDate } from '../../shared/portal-format';
import { ToBeConfirmedModalComponent } from '../../shared/to-be-confirmed-modal/to-be-confirmed-modal.component';
import { RejectModalComponent } from '../../shared/reject-modal/reject-modal.component';
import { PortalDocumentOpener } from '../../shared/portal-document';

@Component({
	selector: 'portal-progress',
	templateUrl: './progress.component.html',
	// Reuse the invoice-row styles (same directory) for the unpaid-invoice cards
	// in the "Your Attention Needed" section.
	styleUrls: ['./progress.component.scss', '../invoices/invoices.component.scss']
})
export class ProgressComponent implements OnInit, OnDestroy {

	private static readonly BAR_COUNT = 20;

	@ViewChild('tbcModal') tbcModal!: ToBeConfirmedModalComponent;
	@ViewChild('rejectModal') rejectModal!: RejectModalComponent;

	// Opens documents in new tabs and reloads the list when the customer returns.
	private docs!: PortalDocumentOpener;
	private sub?: Subscription;

	projectId!: string;
	loading = true;
	error = '';

	progress = 0;
	startDate: string | null = null;
	endDate: string | null = null;
	pendingQuotes: any[] = [];
	unpaidInvoices: any[] = [];
	pendingCount = 0;
	sections: string[] = [];

	bars: boolean[] = [];

	constructor(
		private route: ActivatedRoute,
		private api: PortalApiService,
		private auth: PortalAuthService,
		public preview: PortalPreviewService
	) {}

	// Where the project should be by today, from time elapsed (drives the
	// "Expected today" marker). null when there are no dates. Matches the app's
	// project-overview computeExpectedPct.
	get expectedPct(): number | null {
		if (!this.startDate || !this.endDate) return null;
		const start = new Date(this.startDate).getTime();
		const end = new Date(this.endDate).getTime();
		const today = Date.now();
		if (isNaN(start) || isNaN(end) || end <= start) return null;
		return Math.max(0, Math.min(100, Math.round((today - start) / (end - start) * 100)));
	}

	// Schedule status from actual vs expected (app's deriveScheduleStatus):
	// gap ≥ 15 → behind, ≥ 5 → delayed, else planned.
	get scheduleStatus(): 'planned' | 'delayed' | 'behind' {
		const exp = this.expectedPct;
		if (exp == null) return 'planned';
		const gap = exp - this.progress;
		if (gap >= 15) return 'behind';
		if (gap >= 5) return 'delayed';
		return 'planned';
	}

	get statusHeading(): string {
		return { planned: 'Progressing as planned', delayed: 'Delayed', behind: 'Behind Schedule' }[this.scheduleStatus];
	}

	get statusColor(): string {
		return { planned: 'var(--c-success-solid)', delayed: 'var(--c-warning-solid)', behind: 'var(--c-primary-solid)' }[this.scheduleStatus];
	}

	get statusSubtitle(): string {
		return {
			planned: 'Project is progressing well and on schedule.',
			delayed: 'Some items need attention, delays possible if not resolved.',
			behind: 'Project is behind schedule.'
		}[this.scheduleStatus];
	}

	// A short project note summarising what still needs the customer's attention.
	get projectNotes(): string {
		const q = this.pendingQuotes.length;
		const i = this.unpaidInvoices.length;
		if (!q && !i) return 'Nothing needs your attention right now.';
		const parts: string[] = [];
		if (q) parts.push(`${q} quote${q === 1 ? '' : 's'} pending your review`);
		parts.push(`${i} invoice${i === 1 ? '' : 's'} due for payment`);
		const sentence = parts.length > 1
			? parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1]
			: parts[0];
		return sentence.charAt(0).toUpperCase() + sentence.slice(1) + '.';
	}

	get daysElapsed(): { elapsed: number; total: number } | null {
		if (!this.startDate) return null;
		const start = new Date(this.startDate).getTime();
		const end = this.endDate ? new Date(this.endDate).getTime() : 0;
		const now = Date.now();
		const day = 86400000;
		const elapsed = Math.max(0, Math.round((now - start) / day));
		const total = end ? Math.max(0, Math.round((end - start) / day)) : 0;
		return { elapsed, total };
	}

	ngOnInit(): void {
		// In a preview the document opens by its own URL (the /preview variant) rather than through
		// the portal's document page, which would have no session in an unframed tab.
		this.docs = new PortalDocumentOpener('', () => this.load(),
			this.preview.ready
				? (quoteId, mode, target, done) => this.api.quote.document(
					quoteId, target, mode,
					(res: any) => done(res?.url || null),
					() => done(null))
				: null);

		// Subscribed rather than read once from the snapshot: Angular reuses this
		// component when only the :id changes, so switching project (from the activity
		// feed, or with the browser's back button) would otherwise leave the previous
		// project's content on screen. Resetting here rather than inside load() keeps
		// the opener's refresh-on-return silent.
		this.sub = this.route.params.subscribe(params => {
			this.projectId = params['id'] || '';
			this.docs.projectId = this.projectId;
			this.loading = true;
			this.error = '';
			this.load();
		});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.docs?.destroy();
	}

	load(): void {
		this.api.project.progress(this.projectId, (res: any) => {
			this.progress = res?.progress || 0;
			this.startDate = res?.start_date || null;
			this.endDate = res?.end_date || null;
			this.pendingQuotes = res?.pending?.quotes || [];
			this.unpaidInvoices = res?.pending?.invoices || [];
			this.pendingCount = res?.pending?.count || 0;
			this.sections = res?.sections || [];
			this.buildBars();
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load progress.';
		});
	}

	// --- Quote actions (mirror the Quotes page) ---
	openProposal(q: any): void {
		if (q?.can_view_proposal) this.docs.open(q.id);
	}

	acceptQuote(q: any): void {
		if (q?.can_accept) this.docs.open(q.id, 'accept');
	}

	commentQuote(q: any): void {
		if (q?.can_comment) this.docs.open(q.id, 'comment');
	}

	isPending(q: any): boolean {
		return q.status === 'pending_review' || q.status === 'reviewed' || q.status === 'to_be_confirmed';
	}

	// Status badge — mirrors the Quotes page so both screens read the same.
	badgeClass(status: string): string {
		switch (status) {
			case 'accepted': return 'badge-success';
			case 'declined': return 'badge-danger';
			case 'to_be_confirmed': return 'badge-secondary';
			default: return 'badge-warning'; // pending_review / reviewed
		}
	}

	badgeLabel(status: string): string {
		switch (status) {
			case 'accepted': return 'Accepted';
			case 'declined': return 'Declined';
			case 'to_be_confirmed': return 'To Be Confirmed';
			case 'reviewed': return 'Reviewed';
			default: return 'Pending Review';
		}
	}

	canConfirm(q: any): boolean {
		return q.status === 'pending_review' || q.status === 'reviewed';
	}

	openToBeConfirmed(q: any): void {
		if (!this.canConfirm(q)) return;
		this.tbcModal.open(q);
	}

	onToBeConfirmed(e: any): void {
		const q = e?.quote;
		if (!q) return;
		q.status = 'to_be_confirmed';
		q.to_be_confirmed = true;
	}

	openReject(q: any): void {
		if (!this.isPending(q)) return;
		this.rejectModal.open(q);
	}

	// A rejected quote is no longer awaiting the customer — drop it from the list.
	onRejected(e: any): void {
		const q = e?.quote;
		if (!q) return;
		this.pendingQuotes = this.pendingQuotes.filter(x => x !== q);
		if (this.pendingCount > 0) this.pendingCount--;
	}

	// --- Invoice actions (mirror the Invoices page) ---
	openInvoice(inv: any): void {
		if (inv?.invoice_url) window.open(inv.invoice_url, '_blank', 'noopener');
	}

	payInvoice(inv: any): void {
		if (inv?.pay_url) window.open(inv.pay_url, '_blank', 'noopener');
	}

	// Customer-facing "valid until / overdue" label for a pending quote.
	quoteDateLabel(q: any): { text: string; cls: string } {
		if (q.expiry_date) {
			const overdue = new Date(q.expiry_date) < new Date();
			return overdue
				? { text: `Overdue ${portalDate(q.expiry_date)}`, cls: 'qr-date-overdue' }
				: { text: `Valid until ${portalDate(q.expiry_date)}`, cls: 'qr-date-pending' };
		}
		return { text: '', cls: 'qr-date-pending' };
	}

	// Percentage of an invoice's subtotal (for the paid / to-pay bar).
	invPct(part: number, subtotal: number): number {
		if (!subtotal) return 0;
		return Math.min(100, Math.max(0, (part / subtotal) * 100));
	}

	private buildBars(): void {
		const filled = Math.round((this.progress / 100) * ProgressComponent.BAR_COUNT);
		this.bars = Array.from({ length: ProgressComponent.BAR_COUNT }, (_, i) => i < filled);
	}
}
